import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Drives the "build a 3-sauce bundle" picker: enforces exactly 3 picks total
 * across any combination of cards (repeats allowed), then adds the current
 * (fixed-price bundle) product to cart with line item properties recording
 * which 3 sauces were picked. Adding to cart dispatches the same
 * CartLinesUpdateEvent the rest of the theme uses, so the existing cart
 * drawer opens in place instead of navigating to /cart.
 */
class BundleBuilderComponent extends HTMLElement {
  constructor() {
    super();
    this.requiredCount = Number(this.dataset.requiredCount) || 3;
    this.picks = [];
  }

  connectedCallback() {
    this.grid = this.querySelector('[ref="grid"]');
    this.ringFill = this.querySelector('[ref="ringFill"]');
    this.totalCountEl = this.querySelector('[ref="totalCount"]');
    this.barText = this.querySelector('[ref="barText"]');
    this.addButton = this.querySelector('[ref="addButton"]');
    this.promptText = this.barText?.textContent || '';
    this.bundlePrice = this.dataset.bundlePrice || '0';

    // These live in the hero band, outside this custom element, so query the
    // whole document rather than just this element's own subtree.
    this.heroComparePrice = document.querySelector('[ref="heroComparePrice"]');
    this.barComparePrice = this.querySelector('[ref="barComparePrice"]');

    this.ringCircumference = 119.4;

    this.grid?.addEventListener('click', (event) => this.handleStepClick(event));
    this.addButton?.addEventListener('click', () => this.addBundleToCart());

    this.updateUI();
  }

  handleStepClick(event) {
    const button = event.target.closest('[data-step]');
    if (!button) return;

    const card = button.closest('[data-bundle-card]');
    if (!card) return;

    const delta = Number(button.dataset.step);
    const variantId = card.dataset.variantId;
    const title = card.dataset.title;
    const countEl = card.querySelector('[data-count]');
    const currentCount = Number(countEl.textContent);

    if (delta > 0) {
      if (this.picks.length >= this.requiredCount) return;
      const price = Number(card.dataset.price) || 0;
      this.picks.push({ variantId, title, price });
    } else {
      if (currentCount <= 0) return;
      const lastIndex = this.picks.map((pick) => pick.variantId).lastIndexOf(variantId);
      if (lastIndex !== -1) this.picks.splice(lastIndex, 1);
    }

    countEl.textContent = String(currentCount + delta);
    this.updateUI();
  }

  updateUI() {
    const total = this.picks.length;
    const remaining = this.requiredCount - total;

    if (this.totalCountEl) this.totalCountEl.textContent = String(total);

    if (this.ringFill) {
      const offset = this.ringCircumference * (1 - total / this.requiredCount);
      this.ringFill.style.strokeDashoffset = String(offset);
    }

    this.grid?.querySelectorAll('[data-bundle-card]').forEach((card) => {
      const available = card.dataset.available === 'true';
      const countEl = card.querySelector('[data-count]');
      const count = Number(countEl.textContent);
      const minusButton = card.querySelector('.bundle-card__step--minus');
      const plusButton = card.querySelector('.bundle-card__step--plus');

      if (minusButton) minusButton.disabled = count <= 0;
      if (plusButton) plusButton.disabled = !available || total >= this.requiredCount;
    });

    if (this.addButton) this.addButton.disabled = total !== this.requiredCount;

    if (this.barText) {
      this.barText.textContent =
        total === this.requiredCount
          ? 'All 3 picked — ready to add!'
          : `${remaining} more to pick — ${this.promptText}`;
    }

    this.updateComparePrice(total);
  }

  /**
   * Shows what the 3 picked sauces would cost individually, struck through,
   * next to the fixed bundle price — only once all 3 are picked.
   * @param {number} total
   */
  updateComparePrice(total) {
    const showCompare = total === this.requiredCount;
    const individualTotal = showCompare
      ? Math.round(this.picks.reduce((sum, pick) => sum + pick.price, 0) / 100)
      : 0;

    if (this.heroComparePrice) {
      this.heroComparePrice.hidden = !showCompare;
      if (showCompare) this.heroComparePrice.innerHTML = `<s>${individualTotal} EGP</s>`;
    }

    if (this.barComparePrice) {
      this.barComparePrice.hidden = !showCompare;
      if (showCompare) {
        this.barComparePrice.innerHTML = `<s>${individualTotal} EGP</s> <strong>${this.bundlePrice} EGP</strong>`;
      }
    }
  }

  async addBundleToCart() {
    if (this.picks.length !== this.requiredCount) return;

    this.addButton.disabled = true;
    const originalText = this.addButton.textContent;
    this.addButton.textContent = 'Adding…';

    const properties = {};
    this.picks.forEach((pick, index) => {
      properties[`Sauce ${index + 1}`] = pick.title;
    });

    // Same section ids the rest of the theme's cart drawer/cart-items list
    // for itself, so the drawer's own HTML comes back updated in one round trip.
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    // Dispatched before the fetch resolves — the cart drawer listens for this
    // and opens itself immediately, then fills in once the promise resolves.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: this.dataset.bundleVariantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const fetchCfg = fetchConfig('json', {
        body: JSON.stringify({
          id: this.dataset.bundleVariantId,
          quantity: 1,
          properties,
          sections: sectionIds.join(','),
        }),
      });

      const response = await fetch(this.dataset.addUrl, fetchCfg).then((res) => res.json());

      if (response.status) {
        throw new Error(response.message || 'Add to cart failed');
      }

      const ajaxCart = await fetch(`${this.dataset.cartUrl}.js`).then((res) => res.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'bundle-builder-component',
          sourceId: this.dataset.bundleVariantId,
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });

      this.addButton.textContent = originalText;
      this.resetPicks();
    } catch (error) {
      console.warn('[bundle-builder] add to cart failed:', error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Add to cart failed', code: 'SERVICE_UNAVAILABLE' })
      );
      this.addButton.textContent = originalText;
      this.updateUI();
    }
  }

  resetPicks() {
    this.picks = [];
    this.grid?.querySelectorAll('[data-count]').forEach((countEl) => {
      countEl.textContent = '0';
    });
    this.updateUI();
  }
}

customElements.define('bundle-builder-component', BundleBuilderComponent);
